import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal, { MODAL_FIELD, MODAL_LABEL, MODAL_NOTE, MODAL_ERROR } from './Modal';
import Button from './Button';
import { proService } from '../services';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { SPECIES } from '../theme/species';
import SlotPicker from './SlotPicker';
import { percentFor, totalCents, repeatStarts, repeatOnWeekdays, WEEKDAYS, localDateValue, moveToDate, localTimeValue, moveToTime } from '../utils/proSessionPricing';

/**
 * A local wall-clock date + time as UTC. `new Date('2026-10-10')` is UTC
 * midnight — a day earlier than intended west of London — so the parts are fed
 * to the Date constructor separately and let the browser apply its own offset.
 */
function localDateTime(dateStr, timeStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const [hh, mm] = String(timeStr || '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, hh || 0, mm || 0).toISOString();
}

/**
 * What the offer's time still lacks, as the sentence to show; null when it has a day and a start.
 * A slot from the pro's own times carries both.
 */
export function timeMissing({ slot, eventDate, startTime, endTime }) {
  if (slot?.startsAt) return null;
  if (!eventDate && !startTime) return 'Pick one of their times, or suggest a date and a start time.';
  if (!eventDate) return 'Pick a date for that start time.';
  if (!startTime) return 'Add a start time for that date.';
  if (endTime && endTime <= startTime) return 'The end time has to be after the start time.';
  return null;
}

/**
 * MakeOfferModal — the buyer's opening move (6b). Amount + optional date +
 * message. No money moves here; the card is collected later at Proceed once a
 * price is agreed. On submit we route to the offer's negotiation page.
 */
export default function MakeOfferModal({ isOpen, onClose, slug, businessName, services = [], initialServiceId = null, initialAmountCents = null, initialMessage = null }) {
  const navigate = useNavigate();
  const { profile } = useUser();
  const { user, applyGuestSession } = useAuth();
  const pink = SPECIES.partyStarter;
  const loggedIn = !!user;

  const [amount, setAmount] = useState('');
  const [eventDate, setEventDate] = useState('');
  // Hours, not just a day. "Saturday" isn't something a pro can accept or
  // decline — a 9am lesson and an 8pm reception are different answers.
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  // Set when the requested window is outside what they've said they're open for.
  // Advisory: the request still sends, and the pro decides.
  const [availabilityNote, setAvailabilityNote] = useState('');
  // The slot they chose from the pro's own times.
  const [slot, setSlot] = useState(null);
  // What they're booking. Its length decides which slots fit, so changing it
  // has to clear a slot chosen against the previous one.
  const [serviceId, setServiceId] = useState(initialServiceId);
  // Only services with a length can be booked — without one there's nothing to
  // build slots out of.
  const bookableServices = (services || []).filter((x) => x.isBookable && x.durationMinutes > 0);
  useEffect(() => { setServiceId(initialServiceId); }, [initialServiceId]);
  // Vendor pages: a shop's BOOK / QUOTE row opens the sheet with its from-price and name filled in.
  useEffect(() => {
    if (!isOpen) return;
    if (initialAmountCents) setAmount(String(initialAmountCents / 100));
    if (initialMessage) setMessage(initialMessage);
  }, [isOpen, initialAmountCents, initialMessage]);
  // "None of these work" — a request outside the published times still sends;
  // the pro decides. Refusing it outright just moves the conversation off the
  // platform.
  const [customTime, setCustomTime] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // What is missing from the time, said under the time picker (never a silent disabled button).
  const [timeError, setTimeError] = useState('');

  // Several sessions up front, on a service that takes them. The first session is the time picked
  // above; the rest repeat from it and any of them can be moved.
  const service = bookableServices.find((x) => x.id === serviceId) || null;
  const maxSessions = service?.maxSessions >= 2 ? service.maxSessions : 1;
  const [sessionCount, setSessionCount] = useState(1);
  // 7 · 14 (every N days) or 'days' (CUSTOM DAYS: the weekdays picked below).
  const [everyDays, setEveryDays] = useState(7);
  const [weekdays, setWeekdays] = useState([]);
  const [sessionStarts, setSessionStarts] = useState([]);
  const firstStart = slot ? slot.startsAt : (eventDate && startTime ? localDateTime(eventDate, startTime) : null);
  useEffect(() => { setSessionCount(1); }, [serviceId]);
  const weekdayKey = weekdays.join(',');
  useEffect(() => {
    if (!firstStart || sessionCount < 2) { setSessionStarts([]); return; }
    setSessionStarts(everyDays === 'days'
      ? repeatOnWeekdays(firstStart, sessionCount, weekdayKey ? weekdayKey.split(',').map(Number) : [])
      : repeatStarts(firstStart, sessionCount, everyDays));
  }, [firstStart, sessionCount, everyDays, weekdayKey]);
  // CUSTOM DAYS opens with the first session's own weekday ticked.
  const pickCustomDays = () => {
    setEveryDays('days');
    if (weekdays.length === 0 && firstStart) setWeekdays([new Date(firstStart).getDay()]);
  };
  const toggleWeekday = (d) => setWeekdays((all) => (all.includes(d) ? all.filter((x) => x !== d) : [...all, d]));
  const percentOff = service ? percentFor(service.sessionDiscounts, sessionCount) : 0;
  const listCents = service?.priceCents ? service.priceCents * sessionCount : null;
  const packageCents = listCents ? totalCents(service.priceCents, sessionCount, percentOff) : null;
  // The opening number follows the package; it stays editable, since it is still an offer.
  useEffect(() => { if (packageCents) setAmount(String(packageCents / 100)); }, [packageCents]);

  const reset = () => { setSessionCount(1); setAmount(''); setEventDate(''); setStartTime(''); setEndTime(''); setMessage(''); setEmail(''); setName(''); setError(''); setTimeError(''); };
  const close = () => { if (!submitting) { reset(); onClose(); } };

  const submit = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents < 100) { setError('Enter an offer of at least $1.'); return; }
    // A date with no hour is not something a pro can accept or decline: every offer names a day
    // AND a start time (Nov 2 2026 — testers kept sending bare dates).
    const missing = timeMissing({ slot, eventDate, startTime, endTime });
    if (missing) { setTimeError(missing); setError(''); return; }
    if (sessionCount > 1 && sessionStarts.length !== sessionCount) {
      setError(sessionStarts.length === 0 ? 'Pick a time for your first session.' : 'Pick the days your sessions repeat on.');
      return;
    }
    const buyerEmail = loggedIn ? (profile?.email || '') : email.trim();
    if (!loggedIn && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail)) { setError('Enter a valid email so the pro can reach you.'); return; }
    setSubmitting(true); setError(''); setTimeError('');

    const payload = {
      amountCents: cents,
      email: buyerEmail,
      name: loggedIn ? (profile?.displayName || null) : (name.trim() || null),
      eventDate: eventDate ? localDateTime(eventDate, '00:00') : null,
      // Local wall-clock times, converted to UTC by the browser's own rules.
      startsAt: slot ? slot.startsAt : (eventDate && startTime ? localDateTime(eventDate, startTime) : null),
      endsAt: slot ? slot.endsAt : (eventDate && endTime ? localDateTime(eventDate, endTime) : null),
      tzOffsetMinutes: new Date().getTimezoneOffset(),
      serviceId,
      // In time order: a moved session can now land before an earlier one.
      sessionStarts: sessionCount > 1 ? [...sessionStarts].sort((a, b) => new Date(a) - new Date(b)) : null,
      message: message.trim() || null,
    };

    if (loggedIn) {
      const r = await proService.makeOffer(slug, payload);
      setSubmitting(false);
      if (r.success) {
        // The pro is told too; showing it here means the client isn't surprised
        // when the answer is "that day doesn't work".
        if (r.data?.availabilityWarning) setAvailabilityNote(r.data.availabilityWarning);
        reset(); onClose(); navigate(`/pro/offer/${r.data.id}`);
      } else setError(r.error || 'Could not send your offer.');
      return;
    }

    // Accountless: server mints a stub session; log in with it and continue.
    const r = await proService.makeGuestOffer(slug, payload);
    setSubmitting(false);
    if (r.success) {
      if (r.data.auth) applyGuestSession(r.data.auth);
      reset(); onClose(); navigate(`/pro/offer/${r.data.offer.id}`);
    } else if (r.code === 'EMAIL_REGISTERED') {
      setError('That email already has an account — log in first, then make your offer.');
    } else {
      setError(r.error || 'Could not send your offer.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title={`Make an offer${businessName ? ` — ${businessName}` : ''}`} size="md">
      <div className="space-y-4">
        <div>
          <label className={MODAL_LABEL}>Your offer</label>
          <div className="flex items-center rounded-2xl bg-[#F2F2F2] focus-within:ring-2 focus-within:ring-black">
            <span className="px-3 font-mono text-sm text-gray-500">$</span>
            <input
              type="number" min="1" step="1" inputMode="decimal" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="500"
              className="w-full bg-transparent py-3 pr-3 font-mono text-sm outline-none"
            />
          </div>
        </div>
        {bookableServices.length > 0 && (
          <div>
            <label className={MODAL_LABEL}>
              What do you need
            </label>
            <div className="flex flex-wrap gap-1.5">
              {bookableServices.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={serviceId === s.id}
                  onClick={() => { setServiceId(serviceId === s.id ? null : s.id); setSlot(null); }}
                  className={`rounded-2xl px-3 py-2 text-left font-mono transition-colors ${
                    serviceId === s.id ? 'bg-black text-white' : 'bg-[#F2F2F2] text-black hover:bg-[#E5E5E5]'
                  }`}
                >
                  <span className="block text-[11px] font-bold">{s.descriptionText}</span>
                  <span className={`block text-[9px] uppercase tracking-wide ${serviceId === s.id ? 'text-white/60' : 'text-gray-500'}`}>
                    {s.durationMinutes} min
                    {s.priceCents ? ` · $${(s.priceCents / 100).toFixed(0)}` : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className={MODAL_LABEL}>
            Pick a time
          </label>
          {/* Real slots from their hours, minus busy time, buffers and notice —
              rather than a free-text range nobody actually sells. */}
          <SlotPicker
            slug={slug}
            serviceId={serviceId}
            value={slot}
            onChange={(picked) => {
              setSlot(picked);
              setEventDate(picked.date);
              setAvailabilityNote('');
              setTimeError('');
            }}
          />

          <button
            type="button"
            onClick={() => setCustomTime((v) => !v)}
            className="mt-2 font-mono text-[10px] uppercase tracking-wide text-gray-500 underline hover:text-black"
          >
            {customTime ? 'Pick from their times instead' : 'None of these work — suggest a time'}
          </button>

          {customTime && (
          <div className="mt-2">
          <input
            type="date" aria-label="Suggested date" value={eventDate} onChange={(e) => { setEventDate(e.target.value); setSlot(null); setAvailabilityNote(''); setTimeError(''); }}
            className={MODAL_FIELD}
          />
          {eventDate && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block font-mono text-[10px] font-bold uppercase tracking-wider text-gray-500">From</span>
                <input
                  type="time" value={startTime} onChange={(e) => { setStartTime(e.target.value); setAvailabilityNote(''); setTimeError(''); }}
                  className={`${MODAL_FIELD} py-2`}
                />
              </label>
              <label className="block">
                <span className="mb-1 block font-mono text-[10px] font-bold uppercase tracking-wider text-gray-500">To</span>
                <input
                  type="time" value={endTime} onChange={(e) => { setEndTime(e.target.value); setAvailabilityNote(''); setTimeError(''); }}
                  className={`${MODAL_FIELD} py-2`}
                />
              </label>
            </div>
          )}
          </div>
          )}
          {timeError && (
            <div className={`${MODAL_ERROR} mt-2`} role="alert" data-testid="offer-time-error">{timeError}</div>
          )}
          {availabilityNote && (
            <p className="mt-2 rounded-2xl bg-[#FEF3C7] px-4 py-3 font-mono text-[11px] leading-relaxed text-[#92400E]">
              {availabilityNote}
            </p>
          )}
        </div>
        {maxSessions > 1 && (
          <div data-testid="session-picker">
            <label className={MODAL_LABEL}>How many sessions</label>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: maxSessions }, (_, i) => i + 1).map((n) => {
                const off = percentFor(service.sessionDiscounts, n);
                return (
                  <button key={n} type="button" aria-pressed={sessionCount === n} onClick={() => setSessionCount(n)}
                    className={`rounded-2xl px-3 py-2 font-mono text-[11px] font-bold transition-colors ${sessionCount === n ? 'bg-black text-white' : 'bg-[#F2F2F2] text-black hover:bg-[#E5E5E5]'}`}>
                    {n}{off > 0 ? ` · ${off}% OFF` : ''}
                  </button>
                );
              })}
            </div>
            {sessionCount > 1 && (
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {[[7, 'EVERY WEEK'], [14, 'EVERY 2 WEEKS'], ['days', 'CUSTOM DAYS']].map(([days, label]) => (
                    <button key={days} type="button" aria-pressed={everyDays === days} onClick={() => (days === 'days' ? pickCustomDays() : setEveryDays(days))}
                      className={`rounded-2xl px-3 py-2 font-mono text-[10px] font-bold tracking-wide transition-colors ${everyDays === days ? 'bg-black text-white' : 'bg-[#F2F2F2] text-black hover:bg-[#E5E5E5]'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {everyDays === 'days' && (
                  <div data-testid="custom-days">
                    <div className="flex flex-wrap gap-1" role="group" aria-label="Days your sessions fall on">
                      {WEEKDAYS.map(([d, label]) => (
                        <button key={d} type="button" aria-pressed={weekdays.includes(d)} onClick={() => toggleWeekday(d)}
                          className={`h-9 min-w-[44px] rounded-full px-2 font-mono text-[10px] font-bold tracking-wide transition-colors ${weekdays.includes(d) ? 'bg-black text-white' : 'bg-[#F2F2F2] text-black hover:bg-[#E5E5E5]'}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className={`${MODAL_NOTE} mt-1`}>
                      {weekdays.length === 0
                        ? 'Pick the days your sessions fall on.'
                        : 'After your first session, the next matching days fill in at the same time. Any of them can still be moved.'}
                    </p>
                  </div>
                )}
                {sessionStarts.length === 0 && <p className={MODAL_NOTE}>Pick a time above and your sessions appear here.</p>}
                {/* Every session's day AND start can move (several in one week, an evening one
                    among mornings); its length is the service's. */}
                {sessionStarts.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 font-mono text-[11px]" data-testid={`session-row-${i + 1}`}>
                    <span className="w-20 flex-shrink-0 font-bold">SESSION {i + 1}</span>
                    <input type="date" value={localDateValue(s)} aria-label={`Session ${i + 1} date`} className={`${MODAL_FIELD} min-w-0 py-2`}
                      onChange={(e) => { if (e.target.value) setSessionStarts((all) => all.map((x, k) => (k === i ? moveToDate(x, e.target.value) : x))); }} />
                    <input type="time" value={localTimeValue(s)} aria-label={`Session ${i + 1} start time`} className={`${MODAL_FIELD} w-32 flex-shrink-0 py-2`}
                      onChange={(e) => { if (e.target.value) setSessionStarts((all) => all.map((x, k) => (k === i ? moveToTime(x, e.target.value) : x))); }} />
                  </div>
                ))}
                {service?.durationMinutes > 0 && sessionStarts.length > 0 && (
                  <p className={MODAL_NOTE}>Each session runs {service.durationMinutes} min.</p>
                )}
                {listCents && (
                  <p className="font-mono text-[11px] leading-relaxed" data-testid="package-price">
                    {sessionCount} × ${(service.priceCents / 100).toFixed(2)} = ${(listCents / 100).toFixed(2)}
                    {percentOff > 0 ? ` · ${percentOff}% OFF · $${(packageCents / 100).toFixed(2)}` : ''}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        <div>
          <label className={MODAL_LABEL}>Details</label>
          <textarea
            value={message} maxLength={1000} rows={3} onChange={(e) => setMessage(e.target.value)}
            placeholder="What's the gig? Where, how long, what you're after."
            className={`${MODAL_FIELD} resize-none`}
          />
        </div>

        {/* Accountless: collect contact details — no signup needed, we make a light account for you. */}
        {!loggedIn && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={MODAL_LABEL}>Your name</label>
              <input type="text" value={name} maxLength={120} onChange={(e) => setName(e.target.value)}
                placeholder="First name" className={MODAL_FIELD} />
            </div>
            <div>
              <label className={MODAL_LABEL}>Email</label>
              <input type="email" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" className={MODAL_FIELD} />
            </div>
          </div>
        )}
        {error && <div className={MODAL_ERROR} role="alert">{error}</div>}
        <p className={MODAL_NOTE}>
          No charge yet — you only pay once you both agree on a price. Payment is held in escrow and released after the gig.
        </p>
        <div className="flex gap-2">
          <Button className="flex-1" onClick={submit} disabled={submitting} style={{ background: pink, color: '#000' }}>
            {submitting ? 'Sending…' : 'Send offer'}
          </Button>
          <Button variant="secondary" onClick={close} disabled={submitting}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
}
